import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiService } from "../../services/apiService";

export const fetchDriversPaginated = createAsyncThunk(
  "drivers/fetchDriversPaginated",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getDriversPaginated(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch Drivers");
    }
  }
);

export const fetchDriverExpensesPaginated = createAsyncThunk(
  "drivers/fetchDriverExpensesPaginated",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getDriverExpensesPaginated(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch driver expenses");
    }
  }
);

export const fetchDriverPayroll = createAsyncThunk(
  "drivers/fetchDriverPayroll",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getDriverPayroll(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch driver payroll");
    }
  }
);

export const fetchDriverAttendance = createAsyncThunk(
  "drivers/fetchDriverAttendance",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getDriverAttendance(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch driver attendance");
    }
  }
);

export const fetchDriverLeaves = createAsyncThunk(
  "drivers/fetchDriverLeaves",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getDriverLeaves(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch driver leaves");
    }
  }
);

export const fetchDriverOverview = createAsyncThunk(
  "drivers/fetchDriverOverview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getDriverOverview();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch Driver overview");
    }
  }
);

export const fetchDriverDetail = createAsyncThunk(
  "drivers/fetchDriverDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getDriverDetail(id);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch Driver detail");
    }
  }
);

export const fetchDriverHistory = createAsyncThunk(
  "drivers/fetchDriverHistory",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getDriverHistory(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch Driver history");
    }
  }
);

export const commentOnDriver = createAsyncThunk(
  "drivers/commentOnDriver",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.commentOnDriver(data);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to comment on Driver");
    }
  }
);

export const generateDriverPassword = createAsyncThunk(
  "drivers/generateDriverPassword",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.generateDriverPassword();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to generate Driver password");
    }
  }
);

export const updateDriver = createAsyncThunk(
  "drivers/updateDriver",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiService.updateDriver(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to update Driver");
    }
  }
);

export const updateDriverStatus = createAsyncThunk(
  "drivers/updateDriverStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiService.updateDriverStatus(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to update Driver status");
    }
  }
);

export const createDriver = createAsyncThunk(
  "drivers/createDriver",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.createDriver(data);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to create Driver");
    }
  }
);

export const deleteDriver = createAsyncThunk(
  "drivers/deleteDriver",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.deleteDriver(id);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to delete Driver");
    }
  }
);

const initialState = {
  driverOverviewData: null,
  driverOverviewLoading: false,
  driverOverviewError: null,
  getDriversPaginatedData: null,
  getDriversPaginatedLoading: false,
  getDriversPaginatedError: null,
  getDriverExpensesPaginatedData: null,
  getDriverExpensesPaginatedLoading: false,
  getDriverExpensesPaginatedError: null,
  getDriverPayrollData: null,
  getDriverPayrollLoading: false,
  getDriverPayrollError: null,
  getDriverAttendanceData: null,
  getDriverAttendanceLoading: false,
  getDriverAttendanceError: null,
  getDriverLeavesData: null,
  getDriverLeavesLoading: false,
  getDriverLeavesError: null,
  driverDetail: null,
  getDriverDetailLoading: false,
  getDriverDetailError: null,
  driverHistoryData: [],
  driverHistoryLoading: false,
  driverHistoryError: null,
  commentOnDriverData: null,
  commentOnDriverLoading: false,
  commentOnDriverError: null,
  generateDriverPasswordData: null,
  generateDriverPasswordLoading: false,
  generateDriverPasswordError: null,
  updateDriverData: null,
  updateDriverLoading: false,
  updateDriverError: null,
  updateDriverStatusData: null,
  updateDriverStatusLoading: false,
  updateDriverStatusError: null,
  createDriverLoading: false,
  createDriverError: null,
  deleteDriverLoading: false,
  deleteDriverError: null,
};

const resolveDriverHistoryItems = (payload) => {
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload)) return payload;
  return [];
};

const driverSlice = createSlice({
  name: "drivers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDriverOverview.pending, (state) => {
        state.driverOverviewLoading = true;
        state.driverOverviewError = null;
      })
      .addCase(fetchDriverOverview.fulfilled, (state, action) => {
        state.driverOverviewLoading = false;
        state.driverOverviewData = action.payload?.data || null;
        state.driverOverviewError = null;
      })
      .addCase(fetchDriverOverview.rejected, (state, action) => {
        state.driverOverviewLoading = false;
        state.driverOverviewData = null;
        state.driverOverviewError = action.payload;
      })
      .addCase(fetchDriversPaginated.pending, (state) => {
        state.getDriversPaginatedLoading = true;
        state.getDriversPaginatedError = null;
      })
      .addCase(fetchDriversPaginated.fulfilled, (state, action) => {
        state.getDriversPaginatedLoading = false;
        state.getDriversPaginatedData = action.payload?.data || null;
        state.getDriversPaginatedError = null;
      })
      .addCase(fetchDriversPaginated.rejected, (state, action) => {
        state.getDriversPaginatedLoading = false;
        state.getDriversPaginatedData = null;
        state.getDriversPaginatedError = action.payload;
      })
      .addCase(fetchDriverExpensesPaginated.pending, (state) => {
        state.getDriverExpensesPaginatedLoading = true;
        state.getDriverExpensesPaginatedError = null;
      })
      .addCase(fetchDriverExpensesPaginated.fulfilled, (state, action) => {
        state.getDriverExpensesPaginatedLoading = false;
        state.getDriverExpensesPaginatedData = action.payload?.data || null;
        state.getDriverExpensesPaginatedError = null;
      })
      .addCase(fetchDriverExpensesPaginated.rejected, (state, action) => {
        state.getDriverExpensesPaginatedLoading = false;
        state.getDriverExpensesPaginatedData = null;
        state.getDriverExpensesPaginatedError = action.payload;
      })
      .addCase(fetchDriverPayroll.pending, (state) => {
        state.getDriverPayrollLoading = true;
        state.getDriverPayrollError = null;
      })
      .addCase(fetchDriverPayroll.fulfilled, (state, action) => {
        state.getDriverPayrollLoading = false;
        state.getDriverPayrollData = action.payload?.data || null;
        state.getDriverPayrollError = null;
      })
      .addCase(fetchDriverPayroll.rejected, (state, action) => {
        state.getDriverPayrollLoading = false;
        state.getDriverPayrollData = null;
        state.getDriverPayrollError = action.payload;
      })
      .addCase(fetchDriverAttendance.pending, (state) => {
        state.getDriverAttendanceLoading = true;
        state.getDriverAttendanceError = null;
      })
      .addCase(fetchDriverAttendance.fulfilled, (state, action) => {
        state.getDriverAttendanceLoading = false;
        state.getDriverAttendanceData = action.payload?.data || null;
        state.getDriverAttendanceError = null;
      })
      .addCase(fetchDriverAttendance.rejected, (state, action) => {
        state.getDriverAttendanceLoading = false;
        state.getDriverAttendanceData = null;
        state.getDriverAttendanceError = action.payload;
      })
      .addCase(fetchDriverLeaves.pending, (state) => {
        state.getDriverLeavesLoading = true;
        state.getDriverLeavesError = null;
      })
      .addCase(fetchDriverLeaves.fulfilled, (state, action) => {
        state.getDriverLeavesLoading = false;
        state.getDriverLeavesData = action.payload?.data || null;
        state.getDriverLeavesError = null;
      })
      .addCase(fetchDriverLeaves.rejected, (state, action) => {
        state.getDriverLeavesLoading = false;
        state.getDriverLeavesData = null;
        state.getDriverLeavesError = action.payload;
      })
      .addCase(fetchDriverDetail.pending, (state) => {
        state.getDriverDetailLoading = true;
        state.getDriverDetailError = null;
      })
      .addCase(fetchDriverDetail.fulfilled, (state, action) => {
        state.getDriverDetailLoading = false;
        state.driverDetail = action.payload?.data || null;
        state.getDriverDetailError = null;
      })
      .addCase(fetchDriverDetail.rejected, (state, action) => {
        state.getDriverDetailLoading = false;
        state.driverDetail = null;
        state.getDriverDetailError = action.payload;
      })
      .addCase(fetchDriverHistory.pending, (state) => {
        state.driverHistoryLoading = true;
        state.driverHistoryError = null;
        state.driverHistoryData = [];
      })
      .addCase(fetchDriverHistory.fulfilled, (state, action) => {
        state.driverHistoryLoading = false;
        state.driverHistoryData = resolveDriverHistoryItems(action.payload);
        state.driverHistoryError = null;
      })
      .addCase(fetchDriverHistory.rejected, (state, action) => {
        state.driverHistoryLoading = false;
        state.driverHistoryData = [];
        state.driverHistoryError = action.payload;
      })
      .addCase(commentOnDriver.pending, (state) => {
        state.commentOnDriverLoading = true;
        state.commentOnDriverError = null;
        state.commentOnDriverData = null;
      })
      .addCase(commentOnDriver.fulfilled, (state, action) => {
        state.commentOnDriverLoading = false;
        state.commentOnDriverError = null;
        state.commentOnDriverData = action.payload?.data || action.payload || null;
      })
      .addCase(commentOnDriver.rejected, (state, action) => {
        state.commentOnDriverLoading = false;
        state.commentOnDriverData = null;
        state.commentOnDriverError = action.payload;
      })
      .addCase(generateDriverPassword.pending, (state) => {
        state.generateDriverPasswordLoading = true;
        state.generateDriverPasswordData = null;
        state.generateDriverPasswordError = null;
      })
      .addCase(generateDriverPassword.fulfilled, (state, action) => {
        state.generateDriverPasswordLoading = false;
        state.generateDriverPasswordData = action.payload?.data ?? action.payload ?? null;
        state.generateDriverPasswordError = null;
      })
      .addCase(generateDriverPassword.rejected, (state, action) => {
        state.generateDriverPasswordLoading = false;
        state.generateDriverPasswordData = null;
        state.generateDriverPasswordError = action.payload;
      })
      .addCase(updateDriver.pending, (state) => {
        state.updateDriverLoading = true;
        state.updateDriverData = null;
        state.updateDriverError = null;
      })
      .addCase(updateDriver.fulfilled, (state, action) => {
        state.updateDriverLoading = false;
        state.updateDriverData = action.payload?.data || action.payload || null;
        state.updateDriverError = null;
      })
      .addCase(updateDriver.rejected, (state, action) => {
        state.updateDriverLoading = false;
        state.updateDriverData = null;
        state.updateDriverError = action.payload;
      })
      .addCase(updateDriverStatus.pending, (state) => {
        state.updateDriverStatusLoading = true;
        state.updateDriverStatusData = null;
        state.updateDriverStatusError = null;
      })
      .addCase(updateDriverStatus.fulfilled, (state, action) => {
        state.updateDriverStatusLoading = false;
        state.updateDriverStatusData = action.payload?.data || action.payload || null;
        state.updateDriverStatusError = null;
      })
      .addCase(updateDriverStatus.rejected, (state, action) => {
        state.updateDriverStatusLoading = false;
        state.updateDriverStatusData = null;
        state.updateDriverStatusError = action.payload;
      })
      .addCase(createDriver.pending, (state) => {
        state.createDriverLoading = true;
        state.createDriverError = null;
      })
      .addCase(createDriver.fulfilled, (state) => {
        state.createDriverLoading = false;
        state.createDriverError = null;
      })
      .addCase(createDriver.rejected, (state, action) => {
        state.createDriverLoading = false;
        state.createDriverError = action.payload;
      })
      .addCase(deleteDriver.pending, (state) => {
        state.deleteDriverLoading = true;
        state.deleteDriverError = null;
      })
      .addCase(deleteDriver.fulfilled, (state) => {
        state.deleteDriverLoading = false;
        state.deleteDriverError = null;
      })
      .addCase(deleteDriver.rejected, (state, action) => {
        state.deleteDriverLoading = false;
        state.deleteDriverError = action.payload;
      });
  },
});

export default driverSlice.reducer;
